import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import type { VenueCampaignPerformanceSummaryView } from '@/lib/venue/campaignPerformanceTypes'
import { conversionStatusText } from '@/lib/venue/campaignPerformanceViewModel'
import { readRateText } from '@/lib/venue/campaignViewModel'
import { formatAmount, formatDateTime } from '@/lib/venue/format'

/**
 * 상단 요약 카드(요구 1, #734 §1-2) — 총 발송 건수·평균 열람률·전환 건수/율·실제 과금 금액.
 *
 * 🚨 "총 발송 건수"는 `successCount`(열람률의 분모)로 매핑한다 — 목록 행의 "발송 건수" 컬럼과 같은
 * 필드를 가리켜야 화면 안에서 숫자가 서로 다른 것을 뜻하지 않는다(설계 §자율 결정 2). 발송 완료
 * 캠페인 "수"는 `campaignCount`로 보조 표기한다.
 */
export function PromotionPerformanceSummaryCard({ data }: { data: VenueCampaignPerformanceSummaryView }) {
  const conversionNotice = conversionStatusText(data.conversionTracking)

  return (
    <Card>
      <CardHeader title="홍보 성과·비용 요약" description={`${formatDateTime(data.asOf)} 기준`} />
      <CardBody>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs text-slateish-600">총 발송 건수</dt>
            <dd className="mt-1 text-lg font-semibold text-ink-700">
              {data.successCount.toLocaleString('ko-KR')}건
            </dd>
            <p className="mt-0.5 text-xs text-slateish-400">홍보 {data.campaignCount.toLocaleString('ko-KR')}건</p>
          </div>
          <div>
            <dt className="text-xs text-slateish-600">평균 열람률</dt>
            <dd className="mt-1 text-lg font-semibold text-ink-700">{readRateText(data.readRatePercent)}</dd>
            <p className="mt-0.5 text-xs text-slateish-400">
              {data.readCount.toLocaleString('ko-KR')} / {data.successCount.toLocaleString('ko-KR')}
            </p>
          </div>
          <div>
            <dt className="text-xs text-slateish-600">전환 건수·전환율</dt>
            {conversionNotice ? (
              <dd className="mt-1 text-sm font-medium text-slateish-600">{conversionNotice}</dd>
            ) : (
              <dd className="mt-1 text-lg font-semibold text-ink-700">
                {data.convertedCount.toLocaleString('ko-KR')}건 · {readRateText(data.conversionRatePercent)}
              </dd>
            )}
          </div>
          <div>
            <dt className="text-xs text-slateish-600">실제 과금 금액</dt>
            <dd className="mt-1 text-lg font-semibold text-ink-700">{formatAmount(data.chargedAmount)}</dd>
            <p className="mt-0.5 text-xs text-slateish-400">
              미정산 {formatAmount(data.unsettledAmount)} · 정산완료 {formatAmount(data.settledAmount)}
            </p>
          </div>
        </dl>
      </CardBody>
    </Card>
  )
}
